import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { getNavTheme } from '../../../navigation/clinicNavConfig';
import '../../layout/components/EcxSidebar.css';

function NavIcon({ icon, themeKey, collapsed }) {
    const theme = getNavTheme(themeKey);

    return (
        <span className={`ecx-nav-icon ${theme.iconClass} ${collapsed ? '' : 'me-2'}`}>
            <i className={`bi ${icon}`}></i>
        </span>
    );
}

function NavItem({ to, icon, label, themeKey, collapsed, badge }) {
    const theme = getNavTheme(themeKey);
    const tooltipId = `sms-nav-${to.replace(/\//g, '-')}`;

    return (
        <li className="nav-item">
            <NavLink
                to={to}
                className={({ isActive }) =>
                    `nav-link rounded-3 d-flex align-items-center py-2 px-2 fw-normal ${
                        collapsed ? 'justify-content-center position-relative' : ''
                    } ${isActive ? theme.activeClass : theme.idleClass}`
                }
                data-tooltip-id={collapsed ? tooltipId : undefined}
                data-tooltip-content={collapsed ? label : undefined}
                data-tooltip-place="right"
            >
                <NavIcon icon={icon} themeKey={themeKey} collapsed={collapsed} />
                {!collapsed && <span>{label}</span>}
                {badge > 0 && (
                    <span
                        className={`badge rounded-pill bg-success ${
                            collapsed ? 'position-absolute top-0 end-0' : 'ms-auto'
                        }`}
                        style={{ fontSize: '0.65rem' }}
                    >
                        {badge}
                    </span>
                )}
            </NavLink>
            {collapsed && <Tooltip id={tooltipId} />}
        </li>
    );
}

function SmsSide({ collapsed = false }) {
    const logout = useEyeconxStore((state) => state.logout);
    const smsInbox = useEyeconxStore((state) => state.smsInbox);
    const toggleSmsSidebarCollapsed = useEyeconxStore((state) => state.toggleSmsSidebarCollapsed);
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className={`ecx-sidebar-inner ${collapsed ? 'ecx-sidebar--collapsed' : ''}`}>
            <div className="ecx-sidebar-header">
                <div className="ecx-sidebar-logo">
                    {collapsed ? (
                        <span className="ecx-sidebar-logo-compact d-inline-flex align-items-center justify-content-center">
                            E
                        </span>
                    ) : (
                        <img
                            src="/media/logos2/neweyeconxlogo.png"
                            alt="EyeconX"
                            className="img-fluid"
                        />
                    )}
                </div>
                <button
                    type="button"
                    className="ecx-sidebar-toggle"
                    onClick={toggleSmsSidebarCollapsed}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    title={collapsed ? 'Expand menu' : 'Collapse menu'}
                >
                    <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                </button>
            </div>

            <hr className="mx-2 my-0" />

            <nav className="ecx-sidebar-nav">
                <ul className="nav nav-pills flex-column mb-auto mt-2 gap-1">
                    <NavItem
                        to="sms-dashboard"
                        icon="bi-speedometer2"
                        label="Dashboard"
                        themeKey="primary"
                        collapsed={collapsed}
                    />
                    <NavItem
                        to="sms-messages"
                        icon="bi-chat-left-text"
                        label="Messages"
                        themeKey="success"
                        collapsed={collapsed}
                        badge={smsInbox}
                    />
                    <NavItem
                        to="sms-clinic-settings"
                        icon="bi-gear"
                        label="Settings"
                        themeKey="info"
                        collapsed={collapsed}
                    />
                </ul>
            </nav>

            <div className="ecx-sidebar-footer">
                <hr className="mx-1" />
                <ul className="nav nav-pills flex-column mb-2 gap-1">
                    <NavItem
                        to="sms-whats-new"
                        icon="bi-stars"
                        label="What's New"
                        themeKey="primary"
                        collapsed={collapsed}
                    />
                    <NavItem
                        to="sms-help"
                        icon="bi-life-preserver"
                        label="Help & Support"
                        themeKey="primary"
                        collapsed={collapsed}
                    />
                </ul>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="btn btn-outline-danger ecx-logout-btn w-100 py-2 d-flex align-items-center justify-content-center"
                >
                    <span>Logout</span>
                    <i className="bi bi-box-arrow-right ms-2" />
                </button>

                {!collapsed && (
                    <div className="text-muted text-center mt-3 mb-1">
                        <small className="text-muted" style={{ fontSize: '0.625rem' }}>
                            {new Date().getFullYear()} &copy; EyeconX
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SmsSide;
